import { useEffect, useState, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { collection, query, where, getDocs, doc, onSnapshot, setDoc, getDoc } from 'firebase/firestore'
import { db, auth } from '@/lib/firebase'
import { useAuthState } from '@/hooks/useAuth'
import { useUserData } from '@/hooks/useUserData'
import { DirectBookingCalendarCard } from '@/components/direct-booking/DirectBookingCalendarCard'
import { CREATE_BOOKING_ENDPOINT, CANCEL_BOOKING_ENDPOINT } from '@/config/endpoints'
import type { ArtistAvailability } from '@/types/booking'
import { setHours, setMinutes, format, addDays, startOfToday } from 'date-fns'
import { Timestamp, updateDoc } from 'firebase/firestore'
import { useCalendarSync } from '@/hooks/useCalendarSync'
import { motion } from 'framer-motion'
import { cn } from '@/lib/utils'
import { Eye, EyeOff, Instagram, MapPin, Clock, Calendar, Edit2, User, Bookmark, LogOut, Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { signInWithEmailAndPassword, createUserWithEmailAndPassword } from 'firebase/auth'
import { handleUserData, signOutUser } from '@/lib/firebase'
import { useAnalytics } from '@/hooks/useAnalytics'
import { logAuthAttempt } from '@/lib/securityLogging'
import { trackTikTokSignUp } from '@/lib/tiktokPixel'
import { trackSignUp as trackMetaPixelSignUp } from '@/lib/metaPixel'
import { getDeviceInfo, logDeviceInfoDebug } from '@/lib/deviceInfo'
import { useTheme } from '@/contexts/ThemeContext'

/**
 * Public direct booking page for artists to share with clients via DMs
 * URL format: /book/{linkId}
 */
export default function DirectBookingPage() {
  const { artistId: linkIdParam } = useParams<{ artistId: string }>()
  const navigate = useNavigate()
  const { user } = useAuthState()
  const { userData } = useUserData()
  const { createBookingEvent, isIosCalendar } = useCalendarSync()
  const { trackSignUp, trackSignIn } = useAnalytics()
  const { setThemeMode, currentThemeMode } = useTheme()

  // Force dark mode on this page
  useEffect(() => {
    const previousTheme = currentThemeMode
    setThemeMode('dark')
    
    // Restore previous theme on unmount
    return () => {
      setThemeMode(previousTheme)
    }
  }, [])

  // State
  const [artistId, setArtistId] = useState<string | null>(null)
  const [artistDisplayName, setArtistDisplayName] = useState<string | null>(null)
  const [artistAvatarUrl, setArtistAvatarUrl] = useState<string | null>(null)
  const [artistLocation, setArtistLocation] = useState<{ address?: string; city?: string; country?: string } | null>(null)
  const [artistHasBookingSystem, setArtistHasBookingSystem] = useState(false)
  const [availability, setAvailability] = useState<ArtistAvailability | null>(null)
  const [requiredInformation, setRequiredInformation] = useState<any>(null)
  const [estimatedTime, setEstimatedTime] = useState<number | 'unknown'>('unknown')
  const [preparationDays, setPreparationDays] = useState<number | undefined>(undefined)
  const [existingBooking, setExistingBooking] = useState<any>(null)
  const [checkingExistingBooking, setCheckingExistingBooking] = useState(false)
  const [isCancellingBooking, setIsCancellingBooking] = useState(false)

  // UI state
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<{ start: number; end: number } | null>(null)
  const [tentativeTimeSlot, setTentativeTimeSlot] = useState<{ start: number; end: number } | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [dateDefaultSet, setDateDefaultSet] = useState(false)
  const [isEditingUserInfo, setIsEditingUserInfo] = useState(false)
  const [editValues, setEditValues] = useState({
    instagram: '',
    phoneNumber: ''
  })

  // Auth form state - always default to signup
  const [isAuthLogin, setIsAuthLogin] = useState(false)
  const [authLoading, setAuthLoading] = useState(false)
  const [authError, setAuthError] = useState<string | null>(null)
  const [showPassword, setShowPassword] = useState(false)
  const [authFormData, setAuthFormData] = useState({
    email: '',
    password: '',
    instagramHandle: '',
    phoneNumber: ''
  })
  const [countryCode, setCountryCode] = useState('+358')

  // Fetch booking link, then artist and their booking system on mount
  useEffect(() => {
    const fetchArtistData = async () => {
      if (!linkIdParam) {
        navigate('/artists', { replace: true })
        return
      }

      try {
        setIsLoading(true)

        // First, fetch the booking link to get artistId and link settings
        const bookingLinksRef = collection(db, 'bookingLinks')
        const linkQuery = query(bookingLinksRef, where('linkId', '==', linkIdParam))
        const linkSnapshot = await getDocs(linkQuery)

        if (linkSnapshot.empty) {
          console.error('[DirectBookingPage] Booking link not found:', linkIdParam)
          navigate('/artists', { replace: true })
          return
        }

        const linkDoc = linkSnapshot.docs[0]
        const linkData = linkDoc.data()

        // Check if link is active
        if (!linkData.isActive) {
          console.error('[DirectBookingPage] Booking link is inactive:', linkIdParam)
          navigate('/artists', { replace: true })
          return
        }

        // Check if link is expired (if expiresAt is set)
        if (linkData.expiresAt && linkData.expiresAt.toMillis() < Date.now()) {
          console.error('[DirectBookingPage] Booking link has expired:', linkIdParam)
          navigate('/artists', { replace: true })
          return
        }

        const artistIdFromLink = linkData.artistId

        // Now fetch artist by ID
        const artistDocRef = doc(db, 'artists', artistIdFromLink)
        const artistDoc = await getDoc(artistDocRef)

        if (!artistDoc.exists()) {
          navigate('/artists', { replace: true })
          return
        }

        const artistData = artistDoc.data()
        if (!artistData) {
          navigate('/artists', { replace: true })
          return
        }

        setArtistId(artistIdFromLink)
        setArtistDisplayName(artistData.displayName)
        setArtistAvatarUrl(artistData.avatarUrl || null)
        setArtistLocation(artistData.location || null)

        // Set link-specific settings (override artist defaults)
        setEstimatedTime(linkData.estimatedTime || 'unknown')
        
        // Use preparationDays if available (new way), otherwise fallback to earliestDate (legacy)
        if (linkData.preparationDays !== undefined && linkData.preparationDays !== null) {
          setPreparationDays(linkData.preparationDays)
        } else if (linkData.earliestDate) {
          // Legacy: convert earliestDate to preparationDays
          const legacyEarliestDate = linkData.earliestDate instanceof Timestamp 
            ? linkData.earliestDate.toDate() 
            : linkData.earliestDate
          const today = startOfToday()
          const diffInMs = legacyEarliestDate.getTime() - today.getTime()
          const diffInDays = Math.max(0, Math.ceil(diffInMs / (1000 * 60 * 60 * 24)))
          setPreparationDays(diffInDays)
        } else {
          setPreparationDays(0) // Default to same day
        }

        // Set up real-time listener for booking system
        const bookingSystemRef = doc(db, 'bookingSystems', artistIdFromLink)
        const unsubscribe = onSnapshot(
          bookingSystemRef,
          (bookingDoc) => {
            if (bookingDoc.exists()) {
              const data = bookingDoc.data()
              const subscriptionData = data?.bookingSubscription
              const hasActive = subscriptionData?.status === 'active' || subscriptionData?.status === 'trial'
              setArtistHasBookingSystem(hasActive)

              if (hasActive) {
                setAvailability(data.availability || null)
                
                // Note: We use link-specific estimatedTime and earliestDate (set above)
                // so we don't override them here with artist defaults

                // Check for required information in both formats
                let requiredInfo = null
                if (data.bookingClientInfo && Array.isArray(data.bookingClientInfo)) {
                  requiredInfo = data.bookingClientInfo
                } else if (data.bookingRequiredInformation) {
                  requiredInfo = data.bookingRequiredInformation
                }
                setRequiredInformation(requiredInfo)
              }
            }
          },
          (error) => {
            console.error('Error listening to booking system:', error)
          }
        )

        return () => unsubscribe()
      } catch (error) {
        console.error('[DirectBookingPage] Error fetching artist:', error)
        navigate('/artists', { replace: true })
      } finally {
        setIsLoading(false)
      }
    }

    void fetchArtistData()
  }, [linkIdParam, navigate])

  // Calculate earliestDate dynamically based on preparationDays
  // This ensures the preparation time starts from the current viewing date, not the sending date
  const earliestDate = useMemo(() => {
    if (preparationDays !== undefined && preparationDays !== null) {
      return addDays(startOfToday(), preparationDays)
    }
    return startOfToday() // Fallback to today if no preparationDays
  }, [preparationDays])

  // Check for existing bookings when user logs in
  useEffect(() => {
    const checkForExistingBooking = async () => {
      if (!user || !artistId) return

      try {
        setCheckingExistingBooking(true)
        console.log('[DirectBookingPage] Checking for existing booking:', { userId: user.uid, artistId })

        // Query for active bookings with this artist
        const bookingsRef = collection(db, 'bookings')
        const q = query(
          bookingsRef,
          where('userId', '==', user.uid),
          where('artistId', '==', artistId),
          where('status', 'in', ['pending', 'confirmed', 'scheduled'])
        )

        const querySnapshot = await getDocs(q)
        
        if (!querySnapshot.empty) {
          // Found an existing booking - use the most recent one
          const bookings = querySnapshot.docs.map(doc => {
            const data = doc.data()
            return { id: doc.id, ...data }
          })
          const mostRecent = bookings.sort((a, b) => {
            const aTime = (a as any).scheduledTimestamp?.toMillis() || 0
            const bTime = (b as any).scheduledTimestamp?.toMillis() || 0
            return bTime - aTime
          })[0]
          
          console.log('[DirectBookingPage] Found existing booking:', mostRecent)
          setExistingBooking(mostRecent)
          setSuccess(true)
          
          // Set the selected date and time slot from the booking
          const bookingData = mostRecent as any
          if (bookingData.scheduledTimestamp) {
            const bookingDate = bookingData.scheduledTimestamp.toDate()
            setSelectedDate(bookingDate)
            
            const hours = bookingDate.getHours()
            const minutes = bookingDate.getMinutes()
            const startMinutes = hours * 60 + minutes
            setSelectedTimeSlot({ start: startMinutes, end: startMinutes + 30 })
          }
        } else {
          console.log('[DirectBookingPage] No existing booking found')
        }
      } catch (error) {
        console.error('[DirectBookingPage] Error checking for existing booking:', error)
      } finally {
        setCheckingExistingBooking(false)
      }
    }

    checkForExistingBooking()
  }, [user, artistId])

  const handleCancelBooking = async () => {
    if (!existingBooking) return

    try {
      setIsCancellingBooking(true)
      setError(null)

      console.log('[DirectBookingPage] Cancelling booking:', existingBooking.id)

      const response = await fetch(CANCEL_BOOKING_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          bookingId: existingBooking.id
        })
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        console.error('[DirectBookingPage] Backend error:', errorData)
        throw new Error(errorData.message || `Failed to cancel booking: ${response.statusText}`)
      }

      console.log('[DirectBookingPage] Booking cancelled successfully')
      
      // Clear the booking state but keep selectedDate so calendar stays selected
      setExistingBooking(null)
      setSuccess(false)
      setSelectedTimeSlot(null)
      setTentativeTimeSlot(null)
    } catch (err) {
      console.error('[DirectBookingPage] Error cancelling booking:', err)
      setError(err instanceof Error ? err.message : 'Failed to cancel booking. Please try again.')
    } finally {
      setIsCancellingBooking(false)
    }
  }

  // Set default date to the earliest available date with time slots
  useEffect(() => {
    if (
      !dateDefaultSet &&
      availability &&
      !selectedDate
    ) {
      // Helper to get available time slots for a date (matches useBookingCalendar logic)
      const getAvailableTimeSlots = (date: Date): any[] => {
        const dateString = format(date, 'yyyy-MM-dd')

        // Helper to convert day index to WeekDay
        const getDayName = (d: Date) => {
          const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
          return days[d.getDay()]
        }

        // Check date overrides first (for week mode)
        if (availability.mode === 'week') {
          const dateOverride = availability.dateOverrides?.[dateString]
          if (dateOverride) {
            return dateOverride.isEnabled ? dateOverride.timeSlots : []
          }
        }

        // Check month schedule (for month mode)
        if (availability.mode === 'month') {
          const monthScheduleEntry = availability.monthSchedule?.[dateString]
          if (!monthScheduleEntry) {
            return []
          }
          return monthScheduleEntry.isEnabled ? monthScheduleEntry.timeSlots : []
        }

        // Check week schedule
        const dayName = getDayName(date)
        const weekSchedule = availability.weekSchedule || (availability as any).weeklySchedule
        if (!weekSchedule) return []

        const daySettings = (weekSchedule as Record<string, any>)[dayName]
        return daySettings?.isEnabled ? daySettings.timeSlots : []
      }

      // Helper to get 30-minute intervals (matches useBookingCalendar logic)
      const get30MinuteIntervals = (timeSlots: any[]): any[] => {
        const intervals: any[] = []
        const durationMinutes = estimatedTime === 'unknown' ? 60 : estimatedTime * 60

        timeSlots.forEach(slot => {
          let currentStart = slot.start
          while (currentStart + 30 <= slot.end) {
            const remainingTime = slot.end - currentStart
            if (durationMinutes === 0 || remainingTime >= durationMinutes) {
              intervals.push({
                start: currentStart,
                end: currentStart + 30
              })
            }
            currentStart += 30
          }
        })

        return intervals
      }

      // Helper to check if a date has available 30-minute intervals
      const hasAvailableIntervals = (date: Date): boolean => {
        const slots = getAvailableTimeSlots(date)
        return get30MinuteIntervals(slots).length > 0
      }

      // Find the first date with available intervals starting from earliestDate
      let checkDate = new Date(earliestDate)
      const maxDaysToCheck = 90 // Check up to 90 days ahead
      
      for (let i = 0; i < maxDaysToCheck; i++) {
        if (hasAvailableIntervals(checkDate)) {
          setSelectedDate(checkDate)
          setDateDefaultSet(true)
          break
        }
        checkDate = new Date(checkDate)
        checkDate.setDate(checkDate.getDate() + 1)
      }

      // If no available slots found, still mark as set
      if (!selectedDate) {
        setDateDefaultSet(true)
      }
    }
  }, [availability, earliestDate, selectedDate, dateDefaultSet, estimatedTime])

  const handleBackToCalendar = () => {
    setSelectedTimeSlot(null)
    // Keep selectedDate and tentativeTimeSlot so they remain selected in the calendar
  }

  const handleLogout = async () => {
    try {
      await signOutUser()
      // Clear existing booking state on logout, but keep calendar selection
      setExistingBooking(null)
      setSuccess(false)
      setSelectedTimeSlot(null)
      // Keep selectedDate so calendar stays selected
      // Use linkIdParam to stay on the same booking page
      navigate(`/book/${linkIdParam}`, { replace: true })
    } catch (error) {
      console.error('Error logging out:', error)
      setError('Failed to log out. Please try again.')
    }
  }

  const handleSaveUserInfo = async () => {
    if (!user) return

    try {
      setAuthLoading(true)
      setAuthError(null)

      // Update user document in Firestore
      await setDoc(
        doc(db, 'users', user.uid),
        {
          instagram: editValues.instagram.trim(),
          phoneNumber: editValues.phoneNumber.trim()
        },
        { merge: true }
      )

      // Close edit mode
      setIsEditingUserInfo(false)
    } catch (err) {
      console.error('Error saving user info:', err)
      setAuthError(err instanceof Error ? err.message : 'Failed to save changes. Please try again.')
    } finally {
      setAuthLoading(false)
    }
  }

  const handleAuthSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(authFormData.email.trim())) {
      setAuthError('Invalid email address')
      return
    }
    
    // Validate password length
    if (authFormData.password.length < 6) {
      setAuthError('Password must be at least 6 characters')
      return
    }
    
    // If it's signup, validate phone number and Instagram handle
    if (!isAuthLogin) {
      // Validate phone number
      if (!authFormData.phoneNumber.trim() || authFormData.phoneNumber.trim().length < 6) {
        setAuthError('Invalid phone number')
        return
      }
      
      // Validate Instagram handle
      if (!authFormData.instagramHandle.trim()) {
        setAuthError('Instagram handle is required')
        return
      }
    }
    
    // Proceed with authentication
    setAuthLoading(true)
    setAuthError(null)

    try {
      let userCredential
      
      if (isAuthLogin) {
        userCredential = await signInWithEmailAndPassword(auth, authFormData.email, authFormData.password)
        
        // Get and log device info on login
        const deviceInfo = await getDeviceInfo()
        console.log('[DirectBookingPage] Login - Device Info:', deviceInfo)
        logDeviceInfoDebug(deviceInfo)
        
        // Track sign in
        await trackSignIn('email', 'direct_booking')
        
        // Log successful login
        await logAuthAttempt('login', authFormData.email, userCredential.user.uid)
        
        // For login, just update lastLogin
        await handleUserData(userCredential.user, {})
      } else {
        userCredential = await createUserWithEmailAndPassword(auth, authFormData.email, authFormData.password)
        
        // Get and log device info on signup
        const deviceInfo = await getDeviceInfo()
        console.log('[DirectBookingPage] Signup - Device Info:', deviceInfo)
        logDeviceInfoDebug(deviceInfo)
        
        // Track sign up (non-blocking - don't fail auth if this fails)
        try {
          await trackSignUp('email', 'direct_booking', !!authFormData.instagramHandle.trim())
        } catch (trackError) {
          console.error('Analytics tracking failed (non-critical):', trackError);
        }
        
        // Track TikTok sign up event (non-blocking - don't fail auth if this fails)
        try {
          await trackTikTokSignUp({
            email: authFormData.email,
            userId: userCredential.user.uid,
            content_type: 'email_signup',
            has_instagram: !!authFormData.instagramHandle.trim()
          });
        } catch (ttError) {
          console.error('TikTok pixel tracking failed (non-critical):', ttError);
        }
        
        // Track Meta Pixel sign up event (non-blocking - don't fail auth if this fails)
        try {
          trackMetaPixelSignUp(
            authFormData.email,
            'email',
            !!authFormData.instagramHandle.trim()
          );
        } catch (metaError) {
          console.error('Meta Pixel tracking failed (non-critical):', metaError);
        }
        
        // Log successful signup (non-blocking)
        try {
          await logAuthAttempt('signup', authFormData.email, userCredential.user.uid)
        } catch (logError) {
          console.error('Security logging failed (non-critical):', logError);
        }
        
        // For signup, include createdAt, instagram, and phone number
        const additionalData: { instagram: string; phoneNumber: string; createdAt: boolean } = {
          createdAt: true,
          instagram: authFormData.instagramHandle.trim(),
          phoneNumber: `${countryCode}${authFormData.phoneNumber.trim()}`
        }
        
        // Save user data (non-blocking - don't fail auth if this fails)
        try {
          await handleUserData(userCredential.user, additionalData)
        } catch (userDataError) {
          console.error('User data saving failed (non-critical):', userDataError);
        }
      }

      // Clear form
      setAuthFormData({ email: '', password: '', instagramHandle: '', phoneNumber: '' })
      setCountryCode('+358')
    } catch (error: any) {
      const errorCode = error.code || ''
      let errorMessage = 'An error occurred'
      
      if (errorCode === 'auth/user-not-found' || errorCode === 'auth/wrong-password') {
        errorMessage = 'Invalid email or password'
      } else if (errorCode === 'auth/email-already-in-use') {
        errorMessage = 'Email is already in use'
      } else if (errorCode === 'auth/weak-password') {
        errorMessage = 'Password is too weak'
      } else if (errorCode === 'auth/invalid-email') {
        errorMessage = 'Invalid email address'
      }
      
      setAuthError(errorMessage)
    } finally {
      setAuthLoading(false)
    }
  }

  const isSignupFormValid = (): boolean => {
    return !!(
      authFormData.email.trim() &&
      authFormData.password.trim() &&
      authFormData.instagramHandle.trim() &&
      authFormData.phoneNumber.trim() &&
      countryCode
    )
  }

  const isLoginFormValid = (): boolean => {
    return !!(
      authFormData.email.trim() &&
      authFormData.password.trim()
    )
  }

  const handleBooking = async (formData: Record<string, string | boolean>) => {
    if (!selectedDate || !selectedTimeSlot || !user || !artistId) return

    // Prevent duplicate bookings
    if (existingBooking) {
      setError('You already have a booking with this artist.')
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      // Create timestamp from selected date and time slot
      const hours = Math.floor(selectedTimeSlot.start / 60)
      const minutes = selectedTimeSlot.start % 60
      const bookingDateTime = setMinutes(setHours(selectedDate, hours), minutes)
      const bookingTimestamp = bookingDateTime.getTime()

      // Build clientData from form fields
      const clientData: Record<string, string | boolean> = {}

      if (Array.isArray(requiredInformation)) {
        requiredInformation.forEach((fieldConfig: any, index: number) => {
          const fieldName = `field_${index}`
          const fieldKey = fieldConfig.displayText || fieldConfig.displayName || fieldName
          if (formData[fieldName] !== undefined && formData[fieldName] !== '') {
            clientData[fieldKey] = formData[fieldName]
          }
        })
      } else if (requiredInformation && typeof requiredInformation === 'object') {
        Object.entries(requiredInformation).forEach(([fieldName, fieldConfig]: any) => {
          const fieldKey = fieldConfig.displayName || fieldName
          if (formData[fieldName] !== undefined && formData[fieldName] !== '') {
            clientData[fieldKey] = formData[fieldName]
          }
        })
      }

      // Add auto-populated fields
      if (user?.email) clientData.email = user.email
      if (userData?.instagram) clientData.username = userData.instagram

      const requestData = {
        chatId: null, // Direct bookings don't have a chat ID
        clientData: clientData,
        timestamp: bookingTimestamp,
        bookingDateTime: bookingDateTime.toISOString(),
        artistId: artistId,
        userId: user.uid, // Include userId for the backend to populate missing fields
        estimatedTime: estimatedTime === 'unknown' ? 1 : estimatedTime
      }

      console.log('[DirectBookingPage:handleBooking] 🎫 Booking request payload:', requestData)

      // Call the create_booking endpoint
      console.log('[DirectBookingPage:handleBooking] Sending booking creation request to backend')
      const response = await fetch(CREATE_BOOKING_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData)
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        console.error('[DirectBookingPage:handleBooking] ❌ Backend error:', errorData)
        throw new Error(errorData.message || `Failed to create booking: ${response.statusText}`)
      }

      const result = await response.json()

      // Create iOS Calendar event if iOS Calendar is connected
      if (isIosCalendar && result.bookingId) {
        try {
          const calendarResult = await createBookingEvent({
            clientUsername: userData?.instagram || user?.email || 'Client',
            shortSummary: `Tattoo booking with ${artistDisplayName || 'artist'}`,
            scheduledTimestamp: bookingDateTime,
            estimatedTime: estimatedTime === 'unknown' ? 1 : estimatedTime
          })

          if (calendarResult.eventId) {
            // Store the iOS calendar event ID in the booking document
            try {
              const bookingRef = doc(db, 'bookings', result.bookingId)
              await updateDoc(bookingRef, {
                iosCalendarEventId: calendarResult.eventId
              })
            } catch (updateError) {
              console.error('Failed to save iosCalendarEventId:', updateError)
            }
          }
        } catch (calError) {
          console.error('Failed to create calendar event:', calError)
        }
      }

      setSuccess(true)
      
      // Update the existing booking state to prevent duplicate bookings
      setExistingBooking({
        id: result.bookingId,
        scheduledTimestamp: Timestamp.fromDate(bookingDateTime),
        status: 'pending'
      })
    } catch (err) {
      console.error('[DirectBookingPage] Error creating booking:', err)
      setError(err instanceof Error ? err.message : 'Failed to create booking. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading || checkingExistingBooking) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-foreground mx-auto mb-4"></div>
          <p className="text-foreground">
            {isLoading ? 'Loading booking...' : 'Checking existing bookings...'}
          </p>
        </div>
      </div>
    )
  }

  if (!artistHasBookingSystem) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <h1 className="text-2xl font-bold text-foreground mb-4">Booking Not Available</h1>
          <p className="text-muted-foreground mb-6">
            This artist doesn't have their booking system enabled right now.
          </p>
          <button
            onClick={() => navigate('/artists', { replace: true })}
            className="px-6 py-2 bg-primary text-primary-foreground rounded-md font-medium hover:bg-primary/90 transition-colors"
          >
            View Artists
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="h-screen overflow-hidden bg-background flex flex-col">
      {/* Header */}
      <div className="bg-background flex-shrink-0">
        <div className="max-w-2xl mx-auto px-4 py-2">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4 flex-1 min-w-0">
              {/* Artist Avatar */}
              {artistAvatarUrl ? (
                <img
                  src={artistAvatarUrl}
                  alt={artistDisplayName || 'Artist'}
                  className="w-16 h-16 rounded-[var(--radius)] object-cover flex-shrink-0 shadow-lg border-4 border-background"
                />
              ) : (
                <div className="w-16 h-16 rounded-[var(--radius)] bg-primary/10 flex items-center justify-center flex-shrink-0 shadow-lg border-4 border-background">
                  <span className="text-foreground text-lg font-semibold">
                    {(artistDisplayName || '?').charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
              
              {/* Artist Info */}
              <div className="flex-1 min-w-0">
                <p className="text-sm text-muted-foreground">Book your time with</p>
                <h1 className="text-lg font-semibold text-foreground truncate">
                  {artistDisplayName || 'Artist'}
                </h1>
              </div>
            </div>

            {/* Login/Sign Up Button - Top Right (only during auth stage) */}
            {!user && selectedTimeSlot && (
              <Button
                onClick={() => setIsAuthLogin(!isAuthLogin)}
                variant="default"
                size="sm"
                className="flex-shrink-0"
              >
                {isAuthLogin ? 'Sign Up' : 'Log In'}
              </Button>
            )}
          </div>
        </div>
      </div>


      {/* Main Content */}
      <div className="flex-1 overflow-hidden flex flex-col relative">
        <div className="flex-1 overflow-y-auto max-w-2xl mx-auto w-full px-4 py-2">
        {success ? (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4"
          >
            {/* Main Container - Blue with White Text (matching SimpleAvailabilityMessage) */}
            <motion.div
              className="rounded-[18px] bg-primary text-white p-6"
            >
              {/* Checkmark Icon */}
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center">
                  <Check className="w-8 h-8 text-white" />
                </div>
              </div>

              {/* Title */}
              <h2 className="text-2xl font-bold text-white text-center mb-1">
                Booking Confirmed!
              </h2>
              <p className="text-sm text-white/80 text-center mb-6">
                Your tattoo appointment has been booked and you can see the details below also when revisiting this link!
              </p>
             

              {/* Booking Details */}
              <div className="space-y-3 border-t border-white/20 pt-4">
                {/* Date */}
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-white/70 flex-shrink-0" />
                  <p className="text-sm text-white/90">
                    {selectedDate ? format(selectedDate, 'MMM d, yyyy') : 'No date selected'}
                  </p>
                </div>

                {/* Time */}
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-white/70 flex-shrink-0" />
                  <p className="text-sm text-white/90">
                    {selectedTimeSlot
                      ? format(setHours(setMinutes(new Date(), selectedTimeSlot.start % 60), Math.floor(selectedTimeSlot.start / 60)), 'h:mm a')
                      : 'No time selected'}
                  </p>
                </div>

                {/* Location with Copy Icon */}
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-white/70 flex-shrink-0" />
                    <p className="text-sm text-white/90">
                      {artistLocation?.address || artistLocation?.city || 'Studio'}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      const addressText = artistLocation?.address || artistLocation?.city || 'Studio'
                      navigator.clipboard.writeText(addressText)
                    }}
                    className="p-1.5 hover:bg-white/20 rounded transition-colors flex-shrink-0"
                    type="button"
                    title="Copy address"
                  >
                    <svg className="w-4 h-4 text-white/70 hover:text-white transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3 mt-6">
                {/* Cancel Booking Button */}
                {existingBooking && (
                  <motion.button
                    onClick={handleCancelBooking}
                    disabled={isCancellingBooking}
                    className="w-full py-2.5 px-4 rounded-md text-sm font-semibold bg-white/10 text-white hover:bg-white/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    whileHover={{ scale: isCancellingBooking ? 1 : 1.02 }}
                    whileTap={{ scale: isCancellingBooking ? 1 : 0.98 }}
                  >
                    {isCancellingBooking ? 'Cancelling...' : 'Cancel Booking'}
                  </motion.button>
                )}
              </div>
            </motion.div>
          </motion.div>
        ) : (
          <>
            {!selectedTimeSlot ? (
              // Step 1: Calendar + Time Slots (No login required)
              <>
                <DirectBookingCalendarCard
                  availability={availability}
                  earliestDate={earliestDate}
                  estimatedTime={estimatedTime}
                  selectedDate={selectedDate}
                  selectedTimeSlot={tentativeTimeSlot}
                  onDateSelect={setSelectedDate}
                  onTimeSlotSelect={setTentativeTimeSlot}
                  isLoading={isSubmitting}
                />
                {/* Next Button - Inside Calendar Container */}
                <motion.button
                  onClick={() => {
                    // Only move to next step when Next is clicked
                    if (tentativeTimeSlot) {
                      setSelectedTimeSlot(tentativeTimeSlot)
                    }
                  }}
                  disabled={!tentativeTimeSlot || isSubmitting}
                  className={cn(
                    "w-full py-3 px-4 rounded-md text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed mt-4",
                    tentativeTimeSlot && !isSubmitting
                      ? "bg-primary text-primary-foreground hover:bg-primary/90"
                      : "bg-muted text-muted-foreground"
                  )}
                  type="button"
                  whileHover={{ scale: tentativeTimeSlot && !isSubmitting ? 1.02 : 1 }}
                  whileTap={{ scale: tentativeTimeSlot && !isSubmitting ? 0.98 : 1 }}
                >
                  Next
                </motion.button>
              </>
            ) : selectedTimeSlot ? (
              // Step 2: Show booking details + auth form (if not logged in) or user info (if logged in)
              <>
                {/* Booking Details Section - Always visible */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-card rounded-lg p-4"
                >
                  <h3 className="text-lg font-bold text-foreground mb-3 flex items-center gap-3">
                    <Bookmark className="w-6 h-6 flex-shrink-0" />
                    Booking Details:
                  </h3>
                  <div className="space-y-2 mb-3">
                    {/* Date */}
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-foreground flex-shrink-0" />
                      <p className="text-sm text-foreground">
                        {selectedDate ? format(selectedDate, 'MMM d, yyyy') : 'No date selected'}
                      </p>
                    </div>

                    {/* Time */}
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-foreground flex-shrink-0" />
                      <p className="text-sm text-foreground">
                        {selectedTimeSlot
                          ? format(setHours(setMinutes(new Date(), selectedTimeSlot.start % 60), Math.floor(selectedTimeSlot.start / 60)), 'h:mm a')
                          : 'No time selected'}
                      </p>
                    </div>

                    {/* Location */}
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-foreground flex-shrink-0" />
                      <p className="text-sm text-foreground">
                        {artistLocation?.address || artistLocation?.city || 'Studio'}
                      </p>
                    </div>
                  </div>
                  
                  {/* Back Button */}
                  <motion.button
                    onClick={handleBackToCalendar}
                    disabled={authLoading || isSubmitting}
                    className="w-full py-2 px-4 rounded-md text-sm font-semibold bg-muted hover:bg-muted/80 text-foreground transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    type="button"
                    whileHover={{ scale: (authLoading || isSubmitting) ? 1 : 1.02 }}
                    whileTap={{ scale: (authLoading || isSubmitting) ? 1 : 0.98 }}
                  >
                    Back
                  </motion.button>

                  <div className="space-y-3 mt-4">
                    {!user ? (
                      // Auth form - inline with booking form styling
                      <>
                        {isAuthLogin ? (
                          // Login Form
                          <form onSubmit={handleAuthSubmit} className="space-y-3">
                            {/* Header */}
                            <div className="mb-4">
                              <h3 className="text-lg font-bold text-foreground mb-2 flex items-center gap-3">
                                <User className="w-6 h-6 flex-shrink-0" />
                                Log In
                              </h3>
                              <p className="text-xs text-muted-foreground mt-1">
                                Sign in to complete your booking
                              </p>
                            </div>

                            {/* Email Field */}
                            <div className="space-y-2">
                              <label className="text-xs font-medium text-foreground">Email</label>
                              <Input
                                type="email"
                                value={authFormData.email}
                                onChange={(e) => {
                                  setAuthFormData(prev => ({ ...prev, email: e.target.value }))
                                  if (authError) setAuthError(null)
                                }}
                                placeholder="your@email.com"
                                required
                                className="text-sm"
                              />
                            </div>

                            {/* Password Field */}
                            <div className="space-y-2">
                              <label className="text-xs font-medium text-foreground">Password</label>
                              <div className="relative">
                                <Input
                                  type={showPassword ? "text" : "password"}
                                  value={authFormData.password}
                                  onChange={(e) => {
                                    setAuthFormData(prev => ({ ...prev, password: e.target.value }))
                                    if (authError) setAuthError(null)
                                  }}
                                  placeholder="At least 6 characters"
                                  required
                                  className="text-sm"
                                />
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
                                  onClick={() => setShowPassword(!showPassword)}
                                >
                                  {showPassword ? (
                                    <EyeOff className="h-3 w-3" />
                                  ) : (
                                    <Eye className="h-3 w-3" />
                                  )}
                                </Button>
                              </div>
                            </div>

                            {/* Error Message */}
                            {authError && (
                              <div className="text-xs text-destructive bg-destructive/10 p-2 rounded">
                                {authError}
                              </div>
                            )}

                            {/* Submit Button */}
                            <motion.button
                              type="submit"
                              disabled={authLoading || !isLoginFormValid()}
                              className="w-full py-3 px-4 rounded-md text-sm font-semibold bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              whileHover={{ scale: authLoading ? 1 : 1.02 }}
                              whileTap={{ scale: authLoading ? 1 : 0.98 }}
                            >
                              {authLoading ? 'Loading...' : 'Log In'}
                            </motion.button>
                          </form>
                        ) : (
                          // Signup Form
                          <form onSubmit={handleAuthSubmit} className="space-y-3">
                            {/* Header */}
                            <div className="mb-4">
                              <h3 className="text-lg font-bold text-foreground mb-2 flex items-center gap-3">
                                <User className="w-6 h-6 flex-shrink-0" />
                                Your Information
                              </h3>
                              <p className="text-xs text-muted-foreground mt-1">
                              Create or Log In to your GetMoreTats account to save your booking details ✨
                              </p>
                            </div>

                            {/* Email Field */}
                            <div className="space-y-2">
                              <label className="text-xs font-medium text-foreground">Email:</label>
                              <Input
                                type="email"
                                value={authFormData.email}
                                onChange={(e) => {
                                  setAuthFormData(prev => ({ ...prev, email: e.target.value }))
                                  if (authError) setAuthError(null)
                                }}
                                placeholder="your@email.com"
                                required
                                className="text-sm"
                              />
                            </div>

                            {/* Password Field */}
                            <div className="space-y-2">
                              <label className="text-xs font-medium text-foreground">Password:</label>
                              <div className="relative">
                                <Input
                                  type={showPassword ? "text" : "password"}
                                  value={authFormData.password}
                                  onChange={(e) => {
                                    setAuthFormData(prev => ({ ...prev, password: e.target.value }))
                                    if (authError) setAuthError(null)
                                  }}
                                  placeholder="At least 6 characters"
                                  required
                                  className="text-sm"
                                />
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
                                  onClick={() => setShowPassword(!showPassword)}
                                >
                                  {showPassword ? (
                                    <EyeOff className="h-3 w-3" />
                                  ) : (
                                    <Eye className="h-3 w-3" />
                                  )}
                                </Button>
                              </div>
                            </div>

                            {/* Instagram Handle Field */}
                            <div className="space-y-2">
                              <label className="text-xs font-medium text-foreground">Instagram Handle:</label>
                              <div className="relative">
                                <Instagram className="absolute left-3 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
                                <Input
                                  type="text"
                                  value={authFormData.instagramHandle}
                                  onChange={(e) => {
                                    setAuthFormData(prev => ({ ...prev, instagramHandle: e.target.value }))
                                    if (authError) setAuthError(null)
                                  }}
                                  placeholder="yourhandle"
                                  className="pl-9 text-sm"
                                  required
                                />
                              </div>
                            </div>

                            {/* Phone Number Field */}
                            <div className="space-y-2">
                              <label className="text-xs font-medium text-foreground">Phone Number:</label>
                              <div className="flex gap-2">
                                <select
                                  value={countryCode}
                                  onChange={(e) => setCountryCode(e.target.value)}
                                  className="bg-muted rounded-md px-2 py-2 text-foreground border border-input focus:outline-none focus:ring-0 text-xs"
                                >
                                  <option value="+358">🇫🇮 +358</option>
                                  <option value="+372">🇪🇪 +372</option>
                                  <option value="+31">🇳🇱 +31</option>
                                </select>
                                <Input
                                  type="tel"
                                  value={authFormData.phoneNumber}
                                  onChange={(e) => {
                                    setAuthFormData(prev => ({ ...prev, phoneNumber: e.target.value }))
                                    if (authError) setAuthError(null)
                                  }}
                                  placeholder="1234567890"
                                  className="flex-1 text-sm"
                                  required
                                />
                              </div>
                            </div>

                            {/* Error Message */}
                            {authError && (
                              <div className="text-xs text-destructive bg-destructive/10 p-2 rounded">
                                {authError}
                              </div>
                            )}

                            {/* Submit Button */}
                            <motion.button
                              type="submit"
                              disabled={authLoading || !isSignupFormValid()}
                              className="w-full py-3 px-4 rounded-md text-sm font-semibold bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              whileHover={{ scale: authLoading ? 1 : 1.02 }}
                              whileTap={{ scale: authLoading ? 1 : 0.98 }}
                            >
                              {authLoading ? 'Loading...' : 'Create Account'}
                            </motion.button>
                          </form>
                        )}
                      </>
                    ) : (
                      // Show user info if logged in
                      <>
                        {/* Your Information Section */}
                        <div
                          className="relative"
                        >
                          <div className="flex items-center justify-between mb-3">
                            <h3 className="text-lg font-bold text-foreground flex items-center gap-3">
                              <User className="w-6 h-6 flex-shrink-0" />
                              Your Information:
                            </h3>
                            <div className="flex items-center gap-2">
                              {!isEditingUserInfo && (
                                <button
                                  onClick={() => {
                                    setEditValues({
                                      instagram: userData?.instagram || '',
                                      phoneNumber: (userData as any)?.phoneNumber || ''
                                    })
                                    setIsEditingUserInfo(true)
                                  }}
                                  className="p-1.5 hover:bg-background rounded transition-colors"
                                  type="button"
                                  title="Edit information"
                                >
                                  <Edit2 className="w-4 h-4 text-foreground" />
                                </button>
                              )}
                              <button
                                onClick={handleLogout}
                                className="p-1.5 hover:bg-background rounded transition-colors"
                                type="button"
                                title="Log out"
                              >
                                <LogOut className="w-4 h-4 text-foreground" />
                              </button>
                            </div>
                          </div>

                          <div className="space-y-3">
                            {/* Email Display - Not Editable */}
                            <div>
                              <label className="block text-xs font-medium text-muted-foreground mb-1">
                                Email
                              </label>
                              <p className="text-sm text-foreground">
                                {user?.email}
                              </p>
                            </div>

                            {/* Instagram Handle - Edit or Display */}
                            <div>
                              <label className="block text-xs font-medium text-muted-foreground mb-1">
                                Instagram Handle
                              </label>
                              {isEditingUserInfo ? (
                                <Input
                                  type="text"
                                  value={editValues.instagram}
                                  onChange={(e) => setEditValues(prev => ({ ...prev, instagram: e.target.value }))}
                                  className="text-sm"
                                  placeholder="yourhandle"
                                />
                              ) : (
                                <p className="text-sm text-foreground">
                                  {userData?.instagram || 'Not provided'}
                                </p>
                              )}
                            </div>

                            {/* Phone Number - Edit or Display */}
                            <div>
                              <label className="block text-xs font-medium text-muted-foreground mb-1">
                                Phone Number
                              </label>
                              {isEditingUserInfo ? (
                                <Input
                                  type="tel"
                                  value={editValues.phoneNumber}
                                  onChange={(e) => setEditValues(prev => ({ ...prev, phoneNumber: e.target.value }))}
                                  className="text-sm"
                                  placeholder="Your phone number"
                                />
                              ) : (
                                <p className="text-sm text-foreground">
                                  {(userData as any)?.phoneNumber || 'Not provided'}
                                </p>
                              )}
                            </div>

                            {/* Cancel and Save Buttons - Only show when editing */}
                            {isEditingUserInfo && (
                              <div className="flex gap-2 pt-2">
                                <button
                                  onClick={() => {
                                    setIsEditingUserInfo(false)
                                    setEditValues({
                                      instagram: userData?.instagram || '',
                                      phoneNumber: (userData as any)?.phoneNumber || ''
                                    })
                                  }}
                                  className="flex-1 px-3 py-2 rounded-md text-xs font-medium bg-muted text-foreground hover:bg-muted/80 transition-colors"
                                  type="button"
                                >
                                  Cancel
                                </button>
                                <button
                                  onClick={handleSaveUserInfo}
                                  disabled={authLoading}
                                  className="flex-1 px-3 py-2 rounded-md text-xs font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                  type="button"
                                >
                                  {authLoading ? 'Saving...' : 'Save'}
                                </button>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Error Message */}
                        {error && (
                          <div
                            className="bg-destructive/10 border border-destructive/50 rounded-lg p-3"
                          >
                            <p className="text-sm text-destructive">{error}</p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </motion.div>

                {/* Book Time Button - Below Container */}
                <motion.button
                  onClick={() => {
                    if (user && selectedDate && selectedTimeSlot) {
                      handleBooking({})
                    }
                  }}
                  disabled={!user || authLoading || isSubmitting}
                  className={cn(
                    "w-full py-3 px-4 rounded-md text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed mt-4",
                    user && !authLoading && !isSubmitting
                      ? "bg-primary text-primary-foreground hover:bg-primary/90"
                      : "bg-muted text-muted-foreground"
                  )}
                  type="button"
                  whileHover={{ scale: (user && !authLoading && !isSubmitting) ? 1.02 : 1 }}
                  whileTap={{ scale: (user && !authLoading && !isSubmitting) ? 0.98 : 1 }}
                >
                  {isSubmitting ? 'Booking...' : 'Book Time'}
                </motion.button>
              </>
            ) : null}
          </>
        )}
        </div>
      </div>
    </div>
  )
}
